import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { isSuperAdmin } from "@/utils/roleUtils";

interface AdminGuardProps {
  children: ReactNode;
}

/**
 * Route guard for the admin panel.
 *
 * Deliberately NOT RoleGuard: that one short-circuits to `return children` on
 * local/dev/stg before any auth check at all, which would leave the panel —
 * every user's PII, account suspension, and impersonation-URL minting — wide
 * open in those environments. This checks in every environment, production
 * included, with no VITE_APP_ENV branch.
 *
 * It also checks `super_admin` specifically rather than isAdmin(), which
 * matches the plain `admin` role too.
 *
 * This is UX only. The real gate is `require_super_admin` on every /admin API
 * — hiding a route does not protect the data behind it.
 */
export const AdminGuard = ({ children }: AdminGuardProps) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="h-20 w-20 animate-spin rounded-full border-8 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (!user || !isSuperAdmin(user)) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};
